
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface TeacherBasicInfo {
  id: string;
  code: string;
  name: string;
  nickname: string;
  email: string;
  tier: string;
  gender: string;
  personality: string;
  university: string;
  major: string;
  trial_lesson: string;
  teaching_class: string;
  registration_course: string;
  teaching_style_by_academic: string;
  teaching_style_by_op: string;
  remark_teaching_style: string;
  remark_branch: string;
  time_not_teaching: string;
  status: string;
  is_tax_pnd3: boolean;
}

export interface TeacherAdditionalInfo {
  bank: string;
  bank_account: string;
  address: string;
  uid: string;
}

export interface TeacherSubjectRow {
  subject: string;
  education_stage: string;
  course_type: string;
}

export interface TeacherStyleRow {
  teaching_style: string;
  level: number;
}

export interface TeacherCertificateRow {
  certificate_id: string;
  grade: string;
  status: string;
  certificate_received: boolean;
  note: string;
}

export interface TeacherEditData {
  basic: TeacherBasicInfo;
  additional: TeacherAdditionalInfo;
  subjects: TeacherSubjectRow[];
  specialized: string[];
  teachingStyles: TeacherStyleRow[];
  branches: string[];
  certificates: TeacherCertificateRow[];
  studentProficiency: string[];
}

export const useTeacherEdit = (teacherCode: string) => {
  const queryClient = useQueryClient();
  const client = supabase.schema('teacher' as any);

  const { data, isLoading, error } = useQuery({
    queryKey: ['teacher-edit', teacherCode],
    queryFn: async (): Promise<TeacherEditData> => {
      // Fetch teacher basic info
      const { data: teacher, error: tErr } = await (client as any)
        .from('teachers')
        .select('*')
        .eq('code', teacherCode)
        .single();
      if (tErr) throw tErr;

      const teacherId = teacher.id;

      // Fetch all related data in parallel
      const [infoRes, subjectsRes, specializedRes, stylesRes, branchesRes, certsRes, proficiencyRes] = await Promise.all([
        (client as any).from('teacher_informations').select('*').eq('teacher_id', teacherId).maybeSingle(),
        (client as any).from('teacher_subjects').select('*').eq('teacher_id', teacherId),
        (client as any).from('teacher_specialized_tutorings').select('*').eq('teacher_id', teacherId),
        (client as any).from('teacher_teaching_styles').select('*').eq('teacher_id', teacherId),
        (client as any).from('teacher_branches').select('*').eq('teacher_id', teacherId),
        (client as any).from('teacher_certificates').select('*').eq('teacher_id', teacherId),
        (client as any).from('teacher_student_proficiency_suits').select('*').eq('teacher_id', teacherId),
      ]);

      const info = infoRes.data;

      return {
        basic: {
          id: teacher.id,
          code: teacher.code || '',
          name: teacher.name || '',
          nickname: teacher.nickname || '',
          email: teacher.email || '',
          tier: teacher.tier || '',
          gender: teacher.gender || '',
          personality: teacher.personality || '',
          university: teacher.university || '',
          major: teacher.major || '',
          trial_lesson: teacher.trial_lesson || '',
          teaching_class: teacher.teaching_class || '',
          registration_course: teacher.registration_course || '',
          teaching_style_by_academic: teacher.teaching_style_by_academic || '',
          teaching_style_by_op: teacher.teaching_style_by_op || '',
          remark_teaching_style: teacher.remark_teaching_style || '',
          remark_branch: teacher.remark_branch || '',
          time_not_teaching: teacher.time_not_teaching || '',
          status: teacher.status || '',
          is_tax_pnd3: teacher.is_tax_pnd3 || false,
        },
        additional: {
          bank: info?.bank || '',
          bank_account: info?.bank_account || '',
          address: info?.address || '',
          uid: info?.uid || '',
        },
        subjects: (subjectsRes.data || []).map((s: any) => ({
          subject: s.subject || '',
          education_stage: s.education_stage || '',
          course_type: s.course_type || '',
        })),
        specialized: (specializedRes.data || []).map((s: any) => s.specialized_tutoring),
        teachingStyles: (stylesRes.data || []).map((s: any) => ({
          teaching_style: s.teaching_style || '',
          level: s.level || 0,
        })),
        branches: (branchesRes.data || []).map((b: any) => b.branch),
        certificates: (certsRes.data || []).map((c: any) => ({
          certificate_id: c.certificate_id || '',
          grade: c.grade || '',
          status: c.status || '',
          certificate_received: c.certificate_received || false,
          note: c.note || '',
        })),
        studentProficiency: (proficiencyRes.data || []).map((p: any) => p.student_proficiency),
      };
    },
    enabled: !!teacherCode,
  });

  // Fetch filter options for dropdowns (reuse pattern from useTeacherMatch)
  const { data: filterOptions } = useQuery({
    queryKey: ['teacher-edit-options'],
    queryFn: async () => {
      const [teachersRes, specializedRes, certsRes] = await Promise.all([
        (client as any).from('teachers').select('gender, personality, trial_lesson, teaching_class, tier, status'),
        (client as any).from('teacher_specialized_tutorings').select('specialized_tutoring'),
        (client as any).from('certificates').select('*'),
      ]);

      const teachers: any[] = teachersRes.data || [];
      return {
        genders: [...new Set(teachers.map((t: any) => t.gender).filter(Boolean))].sort(),
        personalities: [...new Set(teachers.map((t: any) => t.personality).filter(Boolean))].sort(),
        trialLessons: [...new Set(teachers.map((t: any) => t.trial_lesson).filter(Boolean))].sort(),
        teachingClasses: [...new Set(teachers.map((t: any) => t.teaching_class).filter(Boolean))].sort(),
        tiers: [...new Set(teachers.map((t: any) => t.tier).filter(Boolean))].sort(),
        statuses: [...new Set(['active', 'inactive', ...teachers.map((t: any) => t.status).filter(Boolean)])].sort(),
        allSpecialized: [...new Set((specializedRes.data || []).map((s: any) => s.specialized_tutoring))].sort(),
        allCertificates: (certsRes.data || []) as Array<{ id: string; name: string }>,
      };
    },
    staleTime: 10 * 60 * 1000,
  });

  const saveMutation = useMutation({
    mutationFn: async (formData: TeacherEditData) => {
      const teacherId = formData.basic.id;

      // Update basic teacher info
      const { error: updateErr } = await (client as any)
        .from('teachers')
        .update({
          name: formData.basic.name,
          nickname: formData.basic.nickname,
          email: formData.basic.email,
          tier: formData.basic.tier || null,
          gender: formData.basic.gender || null,
          personality: formData.basic.personality || null,
          university: formData.basic.university || null,
          major: formData.basic.major || null,
          trial_lesson: formData.basic.trial_lesson || null,
          teaching_class: formData.basic.teaching_class || null,
          registration_course: formData.basic.registration_course || null,
          teaching_style_by_academic: formData.basic.teaching_style_by_academic || null,
          teaching_style_by_op: formData.basic.teaching_style_by_op || null,
          remark_teaching_style: formData.basic.remark_teaching_style || null,
          remark_branch: formData.basic.remark_branch || null,
          time_not_teaching: formData.basic.time_not_teaching || null,
          status: formData.basic.status || null,
          is_tax_pnd3: formData.basic.is_tax_pnd3,
        })
        .eq('id', teacherId);
      if (updateErr) throw updateErr;

      // Upsert additional info
      const { error: infoErr } = await (client as any)
        .from('teacher_informations')
        .upsert({
          teacher_id: teacherId,
          bank: formData.additional.bank || null,
          bank_account: formData.additional.bank_account || null,
          address: formData.additional.address || null,
          uid: formData.additional.uid || null,
        }, { onConflict: 'teacher_id' });
      if (infoErr) throw infoErr;

      // Delete and re-insert related tables
      const deleteAndInsert = async (table: string, rows: any[]) => {
        await (client as any).from(table).delete().eq('teacher_id', teacherId);
        if (rows.length > 0) {
          const { error } = await (client as any).from(table).insert(
            rows.map(r => ({ ...r, teacher_id: teacherId }))
          );
          if (error) throw error;
        }
      };

      await Promise.all([
        deleteAndInsert('teacher_subjects', formData.subjects.filter(s => s.subject).map(s => ({
          subject: s.subject,
          education_stage: s.education_stage || null,
          course_type: s.course_type || null,
        }))),
        deleteAndInsert('teacher_specialized_tutorings', formData.specialized.filter(Boolean).map(s => ({
          specialized_tutoring: s,
        }))),
        deleteAndInsert('teacher_teaching_styles', formData.teachingStyles.filter(s => s.teaching_style).map(s => ({
          teaching_style: s.teaching_style,
          level: s.level,
        }))),
        deleteAndInsert('teacher_branches', formData.branches.filter(Boolean).map(b => ({
          branch: b,
        }))),
        deleteAndInsert('teacher_certificates', formData.certificates.filter(c => c.certificate_id).map(c => ({
          certificate_id: c.certificate_id,
          grade: c.grade || null,
          status: c.status || null,
          certificate_received: c.certificate_received,
          note: c.note || null,
        }))),
        deleteAndInsert('teacher_student_proficiency_suits', formData.studentProficiency.filter(Boolean).map(p => ({
          student_proficiency: p,
        }))),
      ]);
    },
    onSuccess: () => {
      toast({ title: 'บันทึกสำเร็จ', description: 'ข้อมูลครูถูกอัพเดทแล้ว' });
      queryClient.invalidateQueries({ queryKey: ['teacher-edit', teacherCode] });
      queryClient.invalidateQueries({ queryKey: ['teacher-match-data'] });
    },
    onError: (err: any) => {
      toast({ title: 'เกิดข้อผิดพลาด', description: err.message, variant: 'destructive' });
    },
  });

  return {
    data,
    isLoading,
    error,
    filterOptions,
    save: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
  };
};
